import React from 'react'
import Drawer from '@mui/material/Drawer'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import CloseIcon from '@mui/icons-material/Close'
import { styled } from '@mui/material/styles'

// Local replacement for @mike/mike-shared-frontend's MikeSlidingPanel, built on
// MUI's temporary Drawer. The shared component hand-rolled the enter/exit
// animation with a CSS opacity+position transition on a wrapper div, which
// faded the panel and — because the inner content was only mounted on open —
// did not reliably slide in. MUI's Drawer gives a proper Slide transition for
// the panel and a Fade for the backdrop, animating correctly in both
// directions. Same prop surface as MikeSlidingPanel so call sites are a
// drop-in swap.

// Height of the app top bar; the panel sits below it (previously the shared
// component's `--sliding-panel-top` CSS variable).
const TOP_OFFSET = 59
const PANEL_WIDTH = 360

interface IProps {
  titleArea?: React.ReactNode
  contentArea?: React.ReactNode
  actionsArea?: React.ReactNode
  isOpen: boolean
  position?: 'left' | 'right'
  noGrayOverlay?: boolean
  onClose: () => void
}

const TitleBar = styled(Grid)({
  display: 'flex'
})

const TitleHeadings = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2, 2, 0, 2),
  flex: 1
}))

const CloseCell = styled(Grid)(({ theme }) => ({
  color: theme.palette.grey[500]
}))

const SlidingPanel = ({
  titleArea,
  contentArea,
  actionsArea,
  isOpen,
  position = 'right',
  noGrayOverlay = false,
  onClose
}: IProps) => (
  <Drawer
    anchor={position}
    open={isOpen}
    onClose={onClose}
    hideBackdrop={noGrayOverlay}
    PaperProps={{
      sx: {
        width: PANEL_WIDTH,
        maxWidth: '100%',
        top: TOP_OFFSET,
        height: `calc(100% - ${TOP_OFFSET}px)`,
        boxShadow: '-4px 0 8px 0 rgba(0, 0, 0, 0.15)'
      }
    }}
    ModalProps={{
      slotProps: {
        backdrop: {
          sx: {
            top: TOP_OFFSET,
            backgroundColor: 'rgba(0, 0, 0, 0.4)'
          }
        }
      }
    }}
  >
    <DialogTitle sx={{ p: 1 }}>
      <TitleBar
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <TitleHeadings>{titleArea}</TitleHeadings>
        <CloseCell>
          <IconButton aria-label="Close" onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </CloseCell>
      </TitleBar>
    </DialogTitle>
    <DialogContent>{contentArea}</DialogContent>
    {/* Only when it has something to show -- an empty DialogActions still
        reserves its padding, leaving dead space below the content area. */}
    {actionsArea ? (
      <DialogActions sx={{ p: 2 }}>{actionsArea}</DialogActions>
    ) : null}
  </Drawer>
)

export default SlidingPanel
